// src/components/avatar/AvatarBuilder.tsx

import React, { useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonTitle,
  IonToolbar,
  IonGrid,
  IonRow,
  IonCol,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import { close, checkmark } from 'ionicons/icons';
import { ImpactStyle } from '@capacitor/haptics';
import { AvatarPreview } from './AvatarPreview';
import {
  DiceCatAvatar,
  AVATAR_COLORS,
  HATS,
  FACE,
  GLASSES,
  RIGHT_HAND,
  LEFT_HAND,
  BODY,
} from './avatarTypes';

interface AvatarBuilderProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (avatar: DiceCatAvatar) => void;
  currentAvatar?: DiceCatAvatar;
}

const AvatarBuilder: React.FC<AvatarBuilderProps> = ({
  isOpen,
  onDismiss,
  onSave,
  currentAvatar,
}) => {
  const [avatar, setAvatar] = useState<DiceCatAvatar>(
    currentAvatar || { color: '#1E88E5' }
  );

  const handleColorSelect = async (color: string) => {
    setAvatar({ ...avatar, color });
  };

  const handleAccessorySelect = async (type: keyof DiceCatAvatar, value: string | undefined | null) => {
    // If value is null or 'null' string, use undefined to prevent storing invalid values
    const cleanValue = (value === null || value === 'null') ? undefined : value;
    setAvatar({ ...avatar, [type]: cleanValue });
  };

  const handleSave = async () => {
    onSave(avatar);
    onDismiss();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss} className="avatar-customizer-modal">
      <IonHeader>
        <IonToolbar>
          <IonTitle>Customize Your Dice Cat</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      {/* Fixed Avatar Preview */}
      <div className="avatar-preview-container" style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: '#1f2937',
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderBottom: '1px solid var(--ion-color-light-shade)'
      }}>
        <AvatarPreview avatar={avatar} size={150} />
      </div>

      <IonContent
        style={{
          '--padding-bottom': 'calc(env(safe-area-inset-bottom) + 80px)'
        }}
      >
        {/* Color Selection */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Avatar Color</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                {AVATAR_COLORS.map((color) => (
                  <IonCol size="3" key={color.value}>
                    <div
                      onClick={() => handleColorSelect(color.value)}
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: color.value,
                        borderRadius: '8px',
                        border: avatar.color === color.value ? '3px solid var(--ion-color-primary)' : '2px solid #ddd',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        transition: 'transform 0.1s',
                      }}
                      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                      onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      {avatar.color === color.value && (
                        <IonIcon icon={checkmark} style={{ color: color.value === '#FAFAFA' ? '#000' : '#fff', fontSize: '24px' }} />
                      )}
                    </div>
                    <p style={{ fontSize: '12px', textAlign: 'center', marginTop: '4px' }}>{color.name}</p>
                  </IonCol>
                ))}
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>

        {/* Accessories */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Accessories</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem>
                <IonLabel>Hat</IonLabel>
                <IonSelect
                  value={avatar.hat}
                  placeholder="Select a hat"
                  onIonChange={(e) => handleAccessorySelect('hat', e.detail.value)}
                >
                  {HATS.map((hat) => (
                    <IonSelectOption key={hat.name} value={hat.value}>
                      {hat.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel>Face</IonLabel>
                <IonSelect
                  value={avatar.face}
                  placeholder="Select a face"
                  onIonChange={(e) => handleAccessorySelect('face', e.detail.value)}
                >
                  {FACE.map((face) => (
                    <IonSelectOption key={face.name} value={face.value}>
                      {face.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel>Glasses</IonLabel>
                <IonSelect
                  value={avatar.glasses}
                  placeholder="Select glasses"
                  onIonChange={(e) => handleAccessorySelect('glasses', e.detail.value)}
                >
                  {GLASSES.map((glasses) => (
                    <IonSelectOption key={glasses.name} value={glasses.value}>
                      {glasses.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel>Body</IonLabel>
                <IonSelect
                  value={avatar.body}
                  placeholder="Select clothing"
                  onIonChange={(e) => handleAccessorySelect('body', e.detail.value)}
                >
                  {BODY.map((bodyItem) => (
                    <IonSelectOption key={bodyItem.name} value={bodyItem.value}>
                      {bodyItem.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel>Right Hand</IonLabel>
                <IonSelect
                  value={avatar.rightHand}
                  placeholder="Select item"
                  onIonChange={(e) => handleAccessorySelect('rightHand', e.detail.value)}
                >
                  {RIGHT_HAND.map((item) => (
                    <IonSelectOption key={item.name} value={item.value}>
                      {item.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel>Left Hand</IonLabel>
                <IonSelect
                  value={avatar.leftHand}
                  placeholder="Select item"
                  onIonChange={(e) => handleAccessorySelect('leftHand', e.detail.value)}
                >
                  {LEFT_HAND.map((item) => (
                    <IonSelectOption key={item.name} value={item.value}>
                      {item.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        <div style={{ padding: '16px', marginBottom: '80px' }}>
          <IonButton expand="block" onClick={handleSave}>
            Save Avatar
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default AvatarBuilder;